import React, { useState, memo } from 'react';
import { Pie, Line, Bar, Doughnut, Scatter } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  getCategoryColor,
  getProductColor,
  formatCurrency,
  formatPercentage,
  getDayName,
} from './salesReportUtils';

export const SalesReportDashboardView = memo(function SalesReportDashboardView({
  analyticsData,
  chartTextColor,
  uiTextColor,
  startDate,
  endDate,
  categoryHighlightMetric,
  setCategoryHighlightMetric,
  categoryHighlightCount,
  setCategoryHighlightCount,
  productHighlightMetric,
  setProductHighlightMetric,
  productHighlightCount,
  setProductHighlightCount,
}) {
  return (
    <div className="dashboard-container">
      {/* Líneas por categoría y por producto (unidades vendidas por mes) */}
      <div className="stock-vs-sales-chart">
        <div className="chart-card wide">
          <h4>Evolución Mensual por Categoría (Unidades Vendidas)</h4>
          <div style={{ marginBottom: '12px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ color: uiTextColor, fontSize: 14 }}>
              Ordenar por:
            </label>
            <select
              value={categoryHighlightMetric}
              onChange={(e) => setCategoryHighlightMetric(e.target.value)}
              className="filter-input"
              style={{ maxWidth: 220 }}
            >
              <option value="quantity">Mayor cantidad</option>
              <option value="revenue">Mayor ingresos</option>
              <option value="profit">Mayor ganancia</option>
              <option value="margin">Mayor margen %</option>
              <option value="customers">Más clientes</option>
              <option value="roi">Mayor ROI</option>
              <option value="avgMonthly">Mayor prom. mensual</option>
            </select>
            <label style={{ color: uiTextColor, fontSize: 14 }}>
              Cantidad de líneas:
            </label>
            <input
              type="number"
              min={1}
              max={8}
              value={categoryHighlightCount}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (isNaN(val)) return;
                setCategoryHighlightCount(Math.max(1, Math.min(8, val)));
              }}
              className="filter-input"
              style={{ width: 80 }}
            />
          </div>
          <div style={{ width: '100%', height: '420px' }}>
            {(() => {
              const monthSet = new Set();
              Object.values(analyticsData.categoryMonthlyAnalysis || {}).forEach(
                (monthsObj) => {
                  Object.keys(monthsObj || {}).forEach((m) => monthSet.add(m));
                }
              );
              const startMonth = (startDate && String(startDate).length >= 7) ? String(startDate).slice(0, 7) : null;
              const endMonth = (endDate && String(endDate).length >= 7) ? String(endDate).slice(0, 7) : null;
              let months = Array.from(monthSet).sort();
              if (startMonth && endMonth) {
                months = months.filter((m) => m >= startMonth && m <= endMonth);
              }

              const start = new Date(startDate);
              const end = new Date(endDate);
              const numMonthsCat = (isNaN(start.getTime()) || isNaN(end.getTime()))
                ? 1
                : Math.max(1, (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1);

              const categoriesArray = Object.entries(
                analyticsData.categoryAnalysis || {}
              ).map(([name, data]) => {
                const totalRevenue = data.revenue || 0;
                const totalCost = data.cost || 0;
                const totalQuantity = data.quantity || 0;
                const totalProfit = totalRevenue - totalCost;
                const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
                const customerCount = data.customers?.size ?? 0;
                return {
                  name,
                  totalQuantity,
                  totalRevenue,
                  totalCost,
                  totalProfit,
                  profitMargin,
                  customerCount
                };
              });

              const getCategorySortValue = (c) => {
                switch (categoryHighlightMetric) {
                  case 'quantity': return c.totalQuantity;
                  case 'revenue': return c.totalRevenue;
                  case 'profit': return c.totalProfit;
                  case 'margin': return c.profitMargin;
                  case 'customers': return c.customerCount;
                  case 'roi': return c.totalCost > 0 ? (c.totalProfit / c.totalCost) * 100 : 0;
                  case 'avgMonthly': return c.totalQuantity / numMonthsCat;
                  default: return c.totalRevenue;
                }
              };

              const sortedForView = [...categoriesArray].sort((a, b) => getCategorySortValue(b) - getCategorySortValue(a));

              const topCategories = sortedForView.slice(0, 8);

              // Preparar transformación para evitar que los valores pequeños queden "pegados al piso"
              const positiveValues = [];
              topCategories.forEach((cat) => {
                months.forEach((m) => {
                  const q =
                    analyticsData.categoryMonthlyAnalysis?.[cat.name]?.[m]
                      ?.quantity || 0;
                  if (q > 0) positiveValues.push(q);
                });
              });

              const useTransform = positiveValues.length > 1;
              let logMin = 0;
              let logMax = 0;
              if (useTransform) {
                const minPos = Math.min(...positiveValues);
                const maxPos = Math.max(...positiveValues);
                if (minPos > 0 && maxPos > minPos) {
                  logMin = Math.log10(minPos);
                  logMax = Math.log10(maxPos);
                } else {
                  // rango degenerado, no transformamos
                  logMin = 0;
                  logMax = 0;
                }
              }

              const transformValue = (q) => {
                if (!useTransform || q <= 0 || logMax <= logMin) {
                  return q;
                }
                const t = (Math.log10(q) - logMin) / (logMax - logMin);
                // 0.1 del alto mínimo, 1 máximo
                return 0.1 + 0.9 * t;
              };
              
              const highlightCount = Math.max(
                1,
                Math.min(categoryHighlightCount, topCategories.length)
              );
              const highlightSet = new Set(
                sortedForView
                  .slice(0, highlightCount)
                  .map((c) => c.name)
              );

              const datasets = topCategories.map((cat, index) => {
                const isHighlighted = highlightSet.has(cat.name);
                const baseColor = getCategoryColor(cat.name);
                const dimmedColor = 'rgba(148, 163, 184, 0.3)';

                return {
                  label: cat.name,
                  data: months.map((m) => {
                    const q =
                      analyticsData.categoryMonthlyAnalysis?.[cat.name]?.[m]
                        ?.quantity || 0;
                    return transformValue(q);
                  }),
                  borderColor: isHighlighted ? baseColor : dimmedColor,
                  backgroundColor: 'transparent',
                  tension: 0.3,
                  borderWidth: isHighlighted ? 4 : 2,
                  pointRadius: isHighlighted ? 5 : 2
                };
              });

              const data = {
                labels: months,
                datasets
              };

              const options = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    labels: { color: chartTextColor }
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const category = context.dataset.label;
                        const monthKey = context.label;
                        const monthData =
                          analyticsData.categoryMonthlyAnalysis?.[category]?.[
                            monthKey
                          ];
                        const units = monthData?.quantity || 0;
                        return `${category}: ${units} unidades`;
                      },
                      afterBody: (context) => {
                        const item = context[0];
                        if (!item) return [];
                        const category = item.dataset.label;
                        const monthKey = item.label;
                        const monthData =
                          analyticsData.categoryMonthlyAnalysis?.[category]?.[
                            monthKey
                          ];
                        if (!monthData) return [];
                        const revenue = monthData.revenue || 0;
                        const cost = monthData.cost || 0;
                        const profit = revenue - cost;
                        return [
                          '',
                          `Ventas mes: ${formatCurrency(revenue)}`,
                          `Costo mes: ${formatCurrency(cost)}`,
                          `Ganancia mes: ${formatCurrency(profit)}`
                        ];
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    ticks: { color: chartTextColor }
                  },
                  y: {
                    ticks: { color: chartTextColor },
                    beginAtZero: true,
                    max: useTransform ? 1.1 : undefined
                  }
                }
              };

              return <Line data={data} options={options} />;
            })()}
          </div>
        </div>

        <div className="chart-card wide">
          <h4>Evolución Mensual por Producto (Unidades Vendidas)</h4>
          <div style={{ marginBottom: '12px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ color: uiTextColor, fontSize: 14 }}>
              Ordenar por:
            </label>
            <select
              value={productHighlightMetric}
              onChange={(e) => setProductHighlightMetric(e.target.value)}
              className="filter-input"
              style={{ maxWidth: 220 }}
            >
              <option value="quantity">Mayor cantidad</option>
              <option value="revenue">Mayor ingresos</option>
              <option value="profit">Mayor ganancia</option>
              <option value="margin">Mayor margen %</option>
              <option value="customers">Más clientes</option>
              <option value="roi">Mayor ROI</option>
              <option value="avgMonthly">Mayor prom. mensual</option>
            </select>
            <label style={{ color: uiTextColor, fontSize: 14 }}>
              Cantidad de líneas:
            </label>
            <input
              type="number"
              min={1}
              max={15}
              value={productHighlightCount}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (isNaN(val)) return;
                setProductHighlightCount(Math.max(1, Math.min(15, val)));
              }}
              className="filter-input"
              style={{ width: 80 }}
            />
          </div>
          <div style={{ width: '100%', height: '420px' }}>
            {(() => {
              const monthSet = new Set();
              Object.values(analyticsData.productMonthlyAnalysis || {}).forEach(
                (monthsObj) => {
                  Object.keys(monthsObj || {}).forEach((m) => monthSet.add(m));
                }
              );
              const startMonthProd = (startDate && String(startDate).length >= 7) ? String(startDate).slice(0, 7) : null;
              const endMonthProd = (endDate && String(endDate).length >= 7) ? String(endDate).slice(0, 7) : null;
              let months = Array.from(monthSet).sort();
              if (startMonthProd && endMonthProd) {
                months = months.filter((m) => m >= startMonthProd && m <= endMonthProd);
              }

              const start = new Date(startDate);
              const end = new Date(endDate);
              const numMonthsProd = (isNaN(start.getTime()) || isNaN(end.getTime()))
                ? 1
                : Math.max(1, (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1);

              const productsArray = Object.entries(
                analyticsData.productAnalysis || {}
              ).map(([name, data]) => {
                const totalRevenue = data.totalRevenue || 0;
                const totalCost = data.totalCost || 0;
                const totalQuantity = data.totalQuantity || 0;
                const totalProfit = totalRevenue - totalCost;
                const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
                const customerCount = data.customers?.size ?? 0;
                return {
                  name,
                  totalQuantity,
                  totalRevenue,
                  totalCost,
                  totalProfit,
                  profitMargin,
                  customerCount
                };
              });

              if (productsArray.length === 0) {
                return <span>No hay datos de productos en el período seleccionado.</span>;
              }

              const getProductSortValue = (p) => {
                switch (productHighlightMetric) {
                  case 'quantity': return p.totalQuantity;
                  case 'revenue': return p.totalRevenue;
                  case 'profit': return p.totalProfit;
                  case 'margin': return p.profitMargin;
                  case 'customers': return p.customerCount;
                  case 'roi': return p.totalCost > 0 ? (p.totalProfit / p.totalCost) * 100 : 0;
                  case 'avgMonthly': return p.totalQuantity / numMonthsProd;
                  default: return p.totalRevenue;
                }
              };

              const sortedForView = [...productsArray].sort((a, b) => getProductSortValue(b) - getProductSortValue(a));

              const topProducts = sortedForView.slice(0, 15);

              const positiveValues = [];
              topProducts.forEach((prod) => {
                months.forEach((m) => {
                  const q =
                    analyticsData.productMonthlyAnalysis?.[prod.name]?.[m]
                      ?.quantity || 0;
                  if (q > 0) positiveValues.push(q);
                });
              });

              const useTransform = positiveValues.length > 1;
              let logMin = 0;
              let logMax = 0;
              if (useTransform) {
                const minPos = Math.min(...positiveValues);
                const maxPos = Math.max(...positiveValues);
                if (minPos > 0 && maxPos > minPos) {
                  logMin = Math.log10(minPos);
                  logMax = Math.log10(maxPos);
                } else {
                  logMin = 0;
                  logMax = 0;
                }
              }

              const transformValue = (q) => {
                if (!useTransform || q <= 0 || logMax <= logMin) {
                  return q;
                }
                const t = (Math.log10(q) - logMin) / (logMax - logMin);
                return 0.1 + 0.9 * t;
              };

              const highlightCount = Math.max(
                1,
                Math.min(productHighlightCount, topProducts.length)
              );
              const highlightSet = new Set(
                sortedForView
                  .slice(0, highlightCount)
                  .map((p) => p.name)
              );

              const datasets = topProducts.map((prod, index) => {
                const isHighlighted = highlightSet.has(prod.name);
                const baseColor = getProductColor(prod.name);
                const dimmedColor = 'rgba(148, 163, 184, 0.3)';

                return {
                  label: prod.name,
                  data: months.map((m) => {
                    const q =
                      analyticsData.productMonthlyAnalysis?.[prod.name]?.[m]
                        ?.quantity || 0;
                    return transformValue(q);
                  }),
                  borderColor: isHighlighted ? baseColor : dimmedColor,
                  backgroundColor: 'transparent',
                  tension: 0.3,
                  borderWidth: isHighlighted ? 3 : 2,
                  pointRadius: isHighlighted ? 4 : 2
                };
              });

              const data = {
                labels: months,
                datasets
              };

              const options = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    labels: { color: chartTextColor }
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const product = context.dataset.label;
                        const monthKey = context.label;
                        const monthData =
                          analyticsData.productMonthlyAnalysis?.[product]?.[
                            monthKey
                          ];
                        const units = monthData?.quantity || 0;
                        return `${product}: ${units} unidades`;
                      },
                      afterBody: (context) => {
                        const item = context[0];
                        if (!item) return [];
                        const product = item.dataset.label;
                        const monthKey = item.label;
                        const monthData =
                          analyticsData.productMonthlyAnalysis?.[product]?.[
                            monthKey
                          ];
                        if (!monthData) return [];
                        const revenue = monthData.revenue || 0;
                        const cost = monthData.cost || 0;
                        const profit = revenue - cost;
                        return [
                          '',
                          `Ventas mes: ${formatCurrency(revenue)}`,
                          `Costo mes: ${formatCurrency(cost)}`,
                          `Ganancia mes: ${formatCurrency(profit)}`
                        ];
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    ticks: { color: chartTextColor }
                  },
                  y: {
                    ticks: { color: chartTextColor },
                    beginAtZero: true,
                    max: useTransform ? 1.1 : undefined
                  }
                }
              };

              return <Line data={data} options={options} />;
            })()}
          </div>
        </div>
      </div>

      {/* Gráficos: día de la semana y categorías */}
      <div className="charts-grid">
        <div className="chart-card">
          <h4>Ventas por Día de la Semana</h4>
          <Bar data={{
            labels: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
            datasets: [{
              label: 'Ventas por día',
              data: [0, 1, 2, 3, 4, 5, 6].map(i => analyticsData.temporalAnalysis.dayOfWeek[i]?.revenue ?? 0),
              backgroundColor: [
                '#e53e3e', '#d69e2e', '#38b2ac', '#4299e1', 
                '#805ad5', '#dd6b20', '#48bb78'
              ],
              borderRadius: 8
            }]
          }} options={{
            plugins: {
              legend: { labels: { color: chartTextColor } },
              tooltip: {
                callbacks: {
                  afterBody: (context) => {
                    const idx = context[0]?.dataIndex;
                    if (idx == null) return [];
                    const d = analyticsData.temporalAnalysis.dayOfWeek[idx];
                    if (!d) return [];
                    return ['', `Operaciones: ${d.orders ?? 0}`, `Productos vendidos: ${d.products ?? 0}`];
                  }
                }
              }
            },
            scales: {
              x: { ticks: { color: chartTextColor } },
              y: { ticks: { color: chartTextColor } }
            }
          }} />
        </div>

        <div className="chart-card">
          <h4>Ventas por Categoría de Producto</h4>
          {(() => {
            // Ordenar categorías por revenue de mayor a menor
            const categoryEntries = Object.entries(analyticsData.categoryAnalysis)
              .map(([cat, data]) => ({ cat, revenue: data.revenue, quantity: data.quantity }))
              .sort((a, b) => b.revenue - a.revenue)
              .slice(0, 8);
            return (
              <Pie data={{
                labels: categoryEntries.map(e => e.cat),
                datasets: [{
                  data: categoryEntries.map(e => e.revenue),
                  backgroundColor: categoryEntries.map(e => getCategoryColor(e.cat)),
                  borderWidth: 0
                }]
              }} options={{
                plugins: {
                  legend: { 
                    labels: { color: chartTextColor },
                    position: 'right'
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${formatCurrency(value)} (${percentage}%)`;
                      },
                      afterBody: (context) => {
                        const item = context[0];
                        if (!item || !item.label) return [];
                        const entry = categoryEntries.find(e => e.cat === item.label);
                        if (!entry) return [];
                        return ['', `Productos vendidos: ${entry.quantity ?? 0}`];
                      }
                    }
                  }
                }
              }} />
            );
          })()}
        </div>
      </div>
    </div>
  );
});

export const SalesReportDemographicView = memo(function SalesReportDemographicView({
  analyticsData,
  chartTextColor,
}) {
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);

    return (
      <div className="demographic-container">
        <div className="analysis-header">
          <h3>Análisis Demográfico y de Productos por Ubicación</h3>
          <p>Identifica qué productos promocionar en cada región</p>
        </div>

        <div className="demographic-grid">
          <div className="chart-card wide">
            <h4>Ventas por Provincia</h4>
            <Bar data={{
              labels: Object.keys(analyticsData.demographicAnalysis.byProvince).slice(0, 10),
              datasets: [{
                label: 'Ingresos',
                data: Object.values(analyticsData.demographicAnalysis.byProvince).slice(0, 10).map(p => p.revenue),
                backgroundColor: 'rgba(66, 153, 225, 0.8)',
                borderColor: '#4299e1',
                borderWidth: 1
              }, {
                label: 'Número de Clientes',
                data: Object.values(analyticsData.demographicAnalysis.byProvince).slice(0, 10).map(p => p.customerCount),
                backgroundColor: 'rgba(72, 187, 120, 0.8)',
                borderColor: '#48bb78',
                borderWidth: 1,
                yAxisID: 'y1'
              }]
            }} options={{
              plugins: {
                legend: { labels: { color: chartTextColor } },
                tooltip: {
                  callbacks: {
                    afterBody: (context) => {
                      const idx = context[0]?.dataIndex;
                      if (idx == null) return [];
                      const labels = Object.keys(analyticsData.demographicAnalysis.byProvince).slice(0, 10);
                      const province = labels[idx];
                      const data = analyticsData.demographicAnalysis.byProvince[province];
                      if (!data) return [];
                      const products = Object.values(data.products || {}).reduce((s, p) => s + (p.quantity || 0), 0);
                      return ['', `Operaciones: ${data.orders ?? 0}`, `Productos vendidos: ${products}`];
                    }
                  }
                }
              },
              scales: {
                x: { ticks: { color: chartTextColor } },
                y: { 
                  ticks: { color: chartTextColor },
                  beginAtZero: true
                },
                y1: {
                  type: 'linear',
                  display: true,
                  position: 'right',
                  ticks: { color: chartTextColor },
                  grid: { drawOnChartArea: false }
                }
              }
            }} />
          </div>
        </div>

        {/* Selector de Provincia */}
        <div className="location-selector">
          <h4>Selecciona una ubicación para ver productos más vendidos:</h4>
          <select 
            value={selectedProvince || ''} 
            onChange={(e) => setSelectedProvince(e.target.value)}
            className="filter-input"
          >
            <option value="">Seleccionar Provincia...</option>
            {Object.keys(analyticsData.demographicAnalysis.byProvince)
              .sort((a, b) => analyticsData.demographicAnalysis.byProvince[b].revenue - 
                             analyticsData.demographicAnalysis.byProvince[a].revenue)
              .map(province => (
                <option key={province} value={province}>
                  {province} - {formatCurrency(analyticsData.demographicAnalysis.byProvince[province].revenue)}
                </option>
              ))}
          </select>

          <select 
            value={selectedCity || ''} 
            onChange={(e) => setSelectedCity(e.target.value)}
            className="filter-input"
          >
            <option value="">Seleccionar Ciudad...</option>
            {Object.keys(analyticsData.demographicAnalysis.byCity)
              .sort((a, b) => analyticsData.demographicAnalysis.byCity[b].revenue - 
                             analyticsData.demographicAnalysis.byCity[a].revenue)
              .map(city => (
                <option key={city} value={city}>
                  {city} - {formatCurrency(analyticsData.demographicAnalysis.byCity[city].revenue)}
                </option>
              ))}
          </select>
        </div>

        {/* Productos más vendidos por Provincia */}
        {selectedProvince && (
          <div className="products-by-location">
            <h4>Top Productos en {selectedProvince}</h4>
            <div className="charts-grid">
              <div className="chart-card">
                <h5>Por Cantidad Vendida</h5>
                <Pie data={{
                  labels: Object.entries(analyticsData.demographicAnalysis.byProvince[selectedProvince].products)
                    .sort(([,a], [,b]) => b.quantity - a.quantity)
                    .slice(0, 8)
                    .map(([name]) => name.substring(0, 20) + '...'),
                  datasets: [{
                    data: Object.entries(analyticsData.demographicAnalysis.byProvince[selectedProvince].products)
                      .sort(([,a], [,b]) => b.quantity - a.quantity)
                      .slice(0, 8)
                      .map(([,data]) => data.quantity),
                    backgroundColor: Object.entries(analyticsData.demographicAnalysis.byProvince[selectedProvince].products)
                      .sort(([,a], [,b]) => b.quantity - a.quantity)
                      .slice(0, 8)
                      .map(([name]) => getProductColor(name))
                  }]
                }} options={{
                  plugins: {
                    legend: { 
                      labels: { color: chartTextColor },
                      position: 'right'
                    }
                  }
                }} />
              </div>

              <div className="chart-card">
                <h5>Por Ingresos Generados</h5>
                <Bar data={{
                  labels: Object.entries(analyticsData.demographicAnalysis.byProvince[selectedProvince].products)
                    .sort(([,a], [,b]) => b.revenue - a.revenue)
                    .slice(0, 8)
                    .map(([name]) => name.substring(0, 15) + '...'),
                  datasets: [{
                    label: 'Ingresos',
                    data: Object.entries(analyticsData.demographicAnalysis.byProvince[selectedProvince].products)
                      .sort(([,a], [,b]) => b.revenue - a.revenue)
                      .slice(0, 8)
                      .map(([,data]) => data.revenue),
                    backgroundColor: 'rgba(72, 187, 120, 0.8)',
                    borderRadius: 6
                  }]
                }} options={{
                  indexAxis: 'y',
                  plugins: { legend: { labels: { color: chartTextColor } } },
                  scales: { 
                    x: { ticks: { color: chartTextColor } }, 
                    y: { ticks: { color: chartTextColor } } 
                  }
                }} />
              </div>
            </div>

            <div className="table-card">
              <h5>Detalle de Productos en {selectedProvince}</h5>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Cantidad</th>
                      <th>Ingresos</th>
                      <th>Pedidos</th>
                      <th>Promedio/Pedido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(analyticsData.demographicAnalysis.byProvince[selectedProvince].products)
                      .sort(([,a], [,b]) => b.revenue - a.revenue)
                      .slice(0, 15)
                      .map(([product, data]) => (
                        <tr key={product}>
                          <td>{product}</td>
                          <td>{data.quantity}</td>
                          <td>{formatCurrency(data.revenue)}</td>
                          <td>{data.orders}</td>
                          <td>{(data.quantity / data.orders).toFixed(1)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Productos más vendidos por Ciudad */}
        {selectedCity && (
          <div className="products-by-location">
            <h4>Top Productos en {selectedCity}</h4>
            <div className="charts-grid">
              <div className="chart-card">
                <h5>Distribución por Producto</h5>
                <Doughnut data={{
                  labels: Object.entries(analyticsData.demographicAnalysis.byCity[selectedCity].products)
                    .sort(([,a], [,b]) => b.revenue - a.revenue)
                    .slice(0, 6)
                    .map(([name]) => name.substring(0, 20) + '...'),
                  datasets: [{
                    data: Object.entries(analyticsData.demographicAnalysis.byCity[selectedCity].products)
                      .sort(([,a], [,b]) => b.revenue - a.revenue)
                      .slice(0, 6)
                      .map(([,data]) => data.revenue),
                    backgroundColor: Object.entries(analyticsData.demographicAnalysis.byCity[selectedCity].products)
                      .sort(([,a], [,b]) => b.revenue - a.revenue)
                      .slice(0, 6)
                      .map(([name]) => getProductColor(name))
                  }]
                }} options={{
                  plugins: {
                    legend: { 
                      labels: { color: chartTextColor },
                      position: 'bottom'
                    }
                  }
                }} />
              </div>

              <div className="chart-card">
                <h5>Cantidad vs Ingresos</h5>
                <Scatter data={{
                  datasets: [{
                    label: 'Productos',
                    data: Object.entries(analyticsData.demographicAnalysis.byCity[selectedCity].products)
                      .map(([name, data]) => ({
                        x: data.quantity,
                        y: data.revenue,
                        label: name
                      })),
                    backgroundColor: 'rgba(128, 90, 213, 0.6)',
                    borderColor: '#805ad5',
                    pointRadius: 8
                  }]
                }} options={{
                  plugins: { 
                    legend: { labels: { color: chartTextColor } },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const point = context.raw;
                          return `${point.label}: ${point.x} unidades, ${formatCurrency(point.y)}`;
                        }
                      }
                    }
                  },
                  scales: {
                    x: { 
                      title: { display: true, text: 'Cantidad Vendida', color: chartTextColor },
                      ticks: { color: chartTextColor }
                    },
                    y: { 
                      title: { display: true, text: 'Ingresos', color: chartTextColor },
                      ticks: { color: chartTextColor }
                    }
                  }
                }} />
              </div>
            </div>
          </div>
        )}

        <div className="geographic-tables">
          <div className="table-card">
            <h4>Top Provincias por Ventas</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Provincia</th>
                    <th>Ingresos</th>
                    <th>Clientes</th>
                    <th>Pedidos</th>
                    <th>Ticket Prom.</th>
                    <th>Producto Estrella</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analyticsData.demographicAnalysis.byProvince)
                    .sort(([,a], [,b]) => b.revenue - a.revenue)
                    .slice(0, 10)
                    .map(([province, data]) => {
                      const topProduct = Object.entries(data.products)
                        .sort(([,a], [,b]) => b.revenue - a.revenue)[0];
                      return (
                        <tr key={province}>
                          <td>{province}</td>
                          <td>{formatCurrency(data.revenue)}</td>
                          <td>{data.customerCount}</td>
                          <td>{data.orders}</td>
                          <td>{formatCurrency(data.avgOrderValue)}</td>
                          <td>{topProduct ? topProduct[0].substring(0, 20) + '...' : '-'}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="table-card">
            <h4>Top Ciudades por Ventas</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Ciudad</th>
                    <th>Provincia</th>
                    <th>Ingresos</th>
                    <th>Clientes</th>
                    <th>Producto Estrella</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analyticsData.demographicAnalysis.byCity)
                    .sort(([,a], [,b]) => b.revenue - a.revenue)
                    .slice(0, 10)
                    .map(([city, data]) => {
                      const topProduct = Object.entries(data.products)
                        .sort(([,a], [,b]) => b.revenue - a.revenue)[0];
                      return (
                        <tr key={city}>
                          <td>{city}</td>
                          <td>{data.province}</td>
                          <td>{formatCurrency(data.revenue)}</td>
                          <td>{data.customerCount}</td>
                          <td>{topProduct ? topProduct[0].substring(0, 20) + '...' : '-'}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
});

export const SalesReportCustomerView = memo(function SalesReportCustomerView({
  analyticsData,
  chartTextColor,
  customerChannelTab,
  setCustomerChannelTab,
  copyEmailsToClipboard,
}) {
  return (
    <div className="customer-analysis-container">
      <div className="analysis-header">
        <h3>Análisis de Clientes por Canal</h3>
        <p>Base de datos segmentada para campañas de marketing</p>
      </div>

      <div className="customer-kpis">
        <div className="kpi-card">
          <h4>Clientes Pedidos</h4>
          <div className="kpi-value">{Object.keys(analyticsData.customersByChannel.pedidos).length}</div>
          <div className="kpi-subtitle">Ventas online</div>
        </div>
        <div className="kpi-card">
          <h4>Clientes MercadoPago</h4>
          <div className="kpi-value">{Object.keys(analyticsData.customersByChannel.mercadopago).length}</div>
          <div className="kpi-subtitle">Ventas online</div>
        </div>
        <div className="kpi-card">
          <h4>Clientes Físicos</h4>
          <div className="kpi-value">{Object.keys(analyticsData.customersByChannel.fisicas).length}</div>
          <div className="kpi-subtitle">Tienda física</div>
        </div>
      </div>

      {/* Navegación por canal */}
      <div className="channel-tabs">
        <button 
          className={`tab-button ${customerChannelTab === 'pedidos' ? 'active' : ''}`}
          onClick={() => setCustomerChannelTab('pedidos')}
        >
          🛒 Pedidos Web
        </button>
        <button 
          className={`tab-button ${customerChannelTab === 'mercadopago' ? 'active' : ''}`}
          onClick={() => setCustomerChannelTab('mercadopago')}
        >
          💳 MercadoPago
        </button>
        <button 
          className={`tab-button ${customerChannelTab === 'fisicas' ? 'active' : ''}`}
          onClick={() => setCustomerChannelTab('fisicas')}
        >
          🏪 Ventas Físicas
        </button>
      </div>

      {/* Contenido por canal */}
      <div className="channel-content">
        {customerChannelTab === 'pedidos' && (
          <div className="channel-section">
            <div className="channel-header">
              <h4>📧 Clientes de Pedidos Web ({Object.keys(analyticsData.customersByChannel.pedidos).length})</h4>
              <button 
                className="copy-emails-button"
                onClick={() => copyEmailsToClipboard(analyticsData.customersByChannel.pedidos)}
              >
                📋 Copiar Emails
              </button>
            </div>
            <div className="table-card">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Email</th>
                      <th>Teléfono</th>
                      <th>Ubicación</th>
                      <th>Total Gastado</th>
                      <th>Pedidos</th>
                      <th>Última Compra</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(analyticsData.customersByChannel.pedidos)
                      .sort((a, b) => b.totalSpent - a.totalSpent)
                      .map((customer, index) => (
                        <tr key={index}>
                          <td>{customer.name || 'Sin nombre'}</td>
                          <td>{customer.email || 'Sin email'}</td>
                          <td>{customer.phone || '-'}</td>
                          <td>{customer.location}</td>
                          <td className="profit">{formatCurrency(customer.totalSpent)}</td>
                          <td>{customer.orderCount}</td>
                          <td>{customer.lastPurchase instanceof Date && !isNaN(customer.lastPurchase.getTime()) ? customer.lastPurchase.toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {customerChannelTab === 'mercadopago' && (
          <div className="channel-section">
            <div className="channel-header">
              <h4>💰 Clientes de MercadoPago ({Object.keys(analyticsData.customersByChannel.mercadopago).length})</h4>
              <button 
                className="copy-emails-button"
                onClick={() => copyEmailsToClipboard(analyticsData.customersByChannel.mercadopago)}
              >
                📋 Copiar Emails
              </button>
            </div>
            <div className="table-card">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Email</th>
                      <th>Teléfono</th>
                      <th>Ubicación</th>
                      <th>Total Gastado</th>
                      <th>Pedidos</th>
                      <th>Última Compra</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(analyticsData.customersByChannel.mercadopago)
                      .sort((a, b) => b.totalSpent - a.totalSpent)
                      .map((customer, index) => (
                        <tr key={index}>
                          <td>{customer.name || 'Sin nombre'}</td>
                          <td>{customer.email || 'Sin email'}</td>
                          <td>{customer.phone || '-'}</td>
                          <td>{customer.location}</td>
                          <td className="profit">{formatCurrency(customer.totalSpent)}</td>
                          <td>{customer.orderCount}</td>
                          <td>{customer.lastPurchase instanceof Date && !isNaN(customer.lastPurchase.getTime()) ? customer.lastPurchase.toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {customerChannelTab === 'fisicas' && (
          <div className="channel-section">
            <div className="channel-header">
              <h4>🏪 Clientes de Tienda Física ({Object.keys(analyticsData.customersByChannel.fisicas).length})</h4>
              <button 
                className="copy-emails-button"
                onClick={() => copyEmailsToClipboard(analyticsData.customersByChannel.fisicas)}
              >
                📋 Copiar Emails (si disponibles)
              </button>
            </div>
            <div className="table-card">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Email</th>
                      <th>Teléfono</th>
                      <th>Ubicación</th>
                      <th>Total Gastado</th>
                      <th>Pedidos</th>
                      <th>Ticket Prom.</th>
                      <th>Última Compra</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(analyticsData.customersByChannel.fisicas)
                      .sort((a, b) => b.totalSpent - a.totalSpent)
                      .map((customer, index) => (
                        <tr key={index}>
                          <td>{customer.name || 'Cliente Físico'}</td>
                          <td>{customer.email || '-'}</td>
                          <td>{customer.phone || '-'}</td>
                          <td>{customer.location}</td>
                          <td className="profit">{formatCurrency(customer.totalSpent)}</td>
                          <td>{customer.orderCount}</td>
                          <td>{formatCurrency(customer.avgOrderValue)}</td>
                          <td>{customer.lastPurchase instanceof Date && !isNaN(customer.lastPurchase.getTime()) ? customer.lastPurchase.toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Análisis comparativo entre canales */}
      <div className="channel-comparison">
        <h4>Comparación entre Canales</h4>
        <div className="charts-grid">
          <div className="chart-card">
            <h5>Distribución de Clientes por Canal</h5>
            <Pie data={{
              labels: ['Pedidos Web', 'MercadoPago', 'Tienda Física'],
              datasets: [{
                data: [
                  Object.keys(analyticsData.customersByChannel.pedidos).length,
                  Object.keys(analyticsData.customersByChannel.mercadopago).length,
                  Object.keys(analyticsData.customersByChannel.fisicas).length
                ],
                backgroundColor: ['#4299e1', '#48bb78', '#ed8936']
              }]
            }} options={{
              plugins: {
                legend: { labels: { color: chartTextColor } }
              }
            }} />
          </div>

          <div className="chart-card">
            <h5>Valor Promedio por Cliente por Canal</h5>
            <Bar data={{
              labels: ['Pedidos Web', 'MercadoPago', 'Tienda Física'],
              datasets: [{
                label: 'Valor Promedio',
                data: [
                  Object.values(analyticsData.customersByChannel.pedidos).reduce((sum, c) => sum + c.totalSpent, 0) / 
                    Object.keys(analyticsData.customersByChannel.pedidos).length || 0,
                  Object.values(analyticsData.customersByChannel.mercadopago).reduce((sum, c) => sum + c.totalSpent, 0) / 
                    Object.keys(analyticsData.customersByChannel.mercadopago).length || 0,
                  Object.values(analyticsData.customersByChannel.fisicas).reduce((sum, c) => sum + c.totalSpent, 0) / 
                    Object.keys(analyticsData.customersByChannel.fisicas).length || 0
                ],
                backgroundColor: ['rgba(66, 153, 225, 0.8)', 'rgba(72, 187, 120, 0.8)', 'rgba(237, 137, 54, 0.8)'],
                borderRadius: 8
              }]
            }} options={{
              plugins: {
                legend: { labels: { color: chartTextColor } }
              },
              scales: {
                x: { ticks: { color: chartTextColor } },
                y: { ticks: { color: chartTextColor } }
              }
            }} />
          </div>
        </div>
      </div>
    </div>
  );
});

export const SalesReportProductView = memo(function SalesReportProductView({
  analyticsData,
  chartTextColor,
  uiTextColor,
  startDate,
  endDate,
  productTableSort,
  setProductTableSort,
  categoryTableSort,
  setCategoryTableSort,
  detailedAnalysisTab,
  setDetailedAnalysisTab,
  detailedItemsPerPage,
  setDetailedItemsPerPage,
  productTablePage,
  setProductTablePage,
  categoryTablePage,
  setCategoryTablePage,
}) {
  return (
    <div className="product-analysis-container">
      <div className="analysis-header">
        <h3>Análisis Avanzado de Productos</h3>
        <p>Performance, rentabilidad y tendencias por producto</p>
      </div>
      
      <div className="product-charts-grid">
        <div className="chart-card">
          <h4>Top 10 por Ingresos</h4>
          <Bar data={{
            labels: analyticsData.topProducts.byRevenue.map(p => p.name.substring(0, 15) + '...'),
            datasets: [{
              label: 'Ingresos',
              data: analyticsData.topProducts.byRevenue.map(p => p.totalRevenue),
              backgroundColor: 'rgba(66, 153, 225, 0.8)',
              borderRadius: 6
            }]
          }} options={{
            indexAxis: 'y',
            plugins: {
              legend: { labels: { color: chartTextColor } },
              tooltip: {
                callbacks: {
                  afterBody: (context) => {
                    const idx = context[0]?.dataIndex;
                    if (idx == null) return [];
                    const p = analyticsData.topProducts.byRevenue[idx];
                    if (!p) return [];
                    return ['', `Operaciones: ${p.orderCount ?? 0}`, `Unidades vendidas: ${p.totalQuantity ?? 0}`];
                  }
                }
              }
            },
            scales: { x: { ticks: { color: chartTextColor } }, y: { ticks: { color: chartTextColor } } }
          }} />
        </div>

        <div className="chart-card">
          <h4>Top 10 por Rentabilidad</h4>
          <Bar data={{
            labels: analyticsData.topProducts.byProfit.map(p => p.name.substring(0, 15) + '...'),
            datasets: [{
              label: 'Ganancia',
              data: analyticsData.topProducts.byProfit.map(p => p.profit),
              backgroundColor: 'rgba(72, 187, 120, 0.8)',
              borderRadius: 6
            }]
          }} options={{
            indexAxis: 'y',
            plugins: {
              legend: { labels: { color: chartTextColor } },
              tooltip: {
                callbacks: {
                  afterBody: (context) => {
                    const idx = context[0]?.dataIndex;
                    if (idx == null) return [];
                    const p = analyticsData.topProducts.byProfit[idx];
                    if (!p) return [];
                    return ['', `Operaciones: ${p.orderCount ?? 0}`, `Unidades vendidas: ${p.totalQuantity ?? 0}`];
                  }
                }
              }
            },
            scales: { x: { ticks: { color: chartTextColor } }, y: { ticks: { color: chartTextColor } } }
          }} />
        </div>
      </div>

      {(() => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const numMonths = (isNaN(start.getTime()) || isNaN(end.getTime()))
          ? 1
          : Math.max(1, (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1);

        const productsFull = Object.entries(analyticsData.productAnalysis || {}).map(([name, data]) => ({
          name,
          totalQuantity: data.totalQuantity || 0,
          totalRevenue: data.totalRevenue || 0,
          totalCost: data.totalCost || 0,
          profit: (data.totalRevenue || 0) - (data.totalCost || 0),
          profitMargin: (data.totalRevenue || 0) > 0 ? (((data.totalRevenue || 0) - (data.totalCost || 0)) / (data.totalRevenue || 0)) * 100 : 0,
          avgPrice: (data.totalQuantity || 0) > 0 ? (data.totalRevenue || 0) / (data.totalQuantity || 0) : 0,
          customerCount: data.customers?.size ?? 0
        }));

        const getProductSortValue = (p) => {
          switch (productTableSort) {
            case 'quantity': return p.totalQuantity;
            case 'revenue': return p.totalRevenue;
            case 'profit': return p.profit;
            case 'margin': return p.profitMargin;
            case 'customers': return p.customerCount;
            case 'roi': return (p.totalCost || 0) > 0 ? (p.profit / p.totalCost) * 100 : 0;
            case 'avgMonthly': return p.totalQuantity / numMonths;
            default: return p.totalRevenue;
          }
        };

        const getCategorySortValue = (c) => {
          switch (categoryTableSort) {
            case 'quantity': return c.totalQuantity;
            case 'revenue': return c.totalRevenue;
            case 'profit': return c.profit;
            case 'margin': return c.profitMargin;
            case 'customers': return c.customerCount;
            case 'roi': return (c.totalCost || 0) > 0 ? (c.profit / c.totalCost) * 100 : 0;
            case 'avgMonthly': return c.totalQuantity / numMonths;
            default: return c.totalRevenue;
          }
        };

        const sortedProducts = [...productsFull].sort((a, b) => getProductSortValue(b) - getProductSortValue(a));
        const sortedCategories = [...(analyticsData.topCategories?.byRevenue || [])].sort((a, b) => getCategorySortValue(b) - getCategorySortValue(a));
        const productTotalPages = Math.max(1, Math.ceil(sortedProducts.length / detailedItemsPerPage));
        const categoryTotalPages = Math.max(1, Math.ceil(sortedCategories.length / detailedItemsPerPage));
        const safeProductPage = Math.min(productTablePage, productTotalPages);
        const safeCategoryPage = Math.min(categoryTablePage, categoryTotalPages);
        const paginatedProducts = sortedProducts.slice(
          (safeProductPage - 1) * detailedItemsPerPage,
          safeProductPage * detailedItemsPerPage
        );
        const paginatedCategories = sortedCategories.slice(
          (safeCategoryPage - 1) * detailedItemsPerPage,
          safeCategoryPage * detailedItemsPerPage
        );
        const isProductView = detailedAnalysisTab === 'product';
        const activePage = isProductView ? safeProductPage : safeCategoryPage;
        const activeTotalPages = isProductView ? productTotalPages : categoryTotalPages;

        const sortOptions = [
          { value: 'quantity', label: 'Mayor cantidad' },
          { value: 'revenue', label: 'Mayor ingresos' },
          { value: 'profit', label: 'Mayor ganancia' },
          { value: 'margin', label: 'Mayor margen %' },
          { value: 'customers', label: 'Más clientes' },
          { value: 'roi', label: 'Mayor ROI' },
          { value: 'avgMonthly', label: 'Mayor prom. mensual' }
        ];

        return (
          <>
      <div className="product-table">
        <h4>{detailedAnalysisTab === 'product' ? 'Análisis Detallado por Producto' : 'Análisis Detallado por Categoría'}</h4>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            className={`tab-button ${detailedAnalysisTab === 'product' ? 'active' : ''}`}
            onClick={() => setDetailedAnalysisTab('product')}
          >
            Por producto
          </button>
          <button
            type="button"
            className={`tab-button ${detailedAnalysisTab === 'category' ? 'active' : ''}`}
            onClick={() => setDetailedAnalysisTab('category')}
          >
            Por categoría
          </button>
          <span style={{ color: uiTextColor, fontSize: 14 }}>Ordenar por:</span>
          <select
            value={detailedAnalysisTab === 'product' ? productTableSort : categoryTableSort}
            onChange={(e) => {
              if (detailedAnalysisTab === 'product') {
                setProductTableSort(e.target.value);
                setProductTablePage(1);
              } else {
                setCategoryTableSort(e.target.value);
                setCategoryTablePage(1);
              }
            }}
            className="filter-input"
            style={{ maxWidth: 220 }}
          >
            {sortOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span style={{ color: uiTextColor, fontSize: 14 }}>Productos por página:</span>
          <select
            value={detailedItemsPerPage}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              setDetailedItemsPerPage(value);
              setProductTablePage(1);
              setCategoryTablePage(1);
            }}
            className="filter-input"
            style={{ maxWidth: 140 }}
          >
            {[5, 10, 15, 20, 30].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
        <div className="table-container">
          {detailedAnalysisTab === 'product' ? (
            <table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Prom. mensual</th>
                  <th>Ingresos</th>
                  <th>Costos</th>
                  <th>Ganancia</th>
                  <th>Margen %</th>
                  <th>Precio Prom.</th>
                  <th>Clientes</th>
                  <th>ROI</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.map(product => (
                  <tr key={product.name}>
                    <td>{product.name}</td>
                    <td>{product.totalQuantity}</td>
                    <td>{(product.totalQuantity / numMonths).toFixed(1)}</td>
                    <td>{formatCurrency(product.totalRevenue)}</td>
                    <td>{formatCurrency(product.totalCost)}</td>
                    <td className="profit">{formatCurrency(product.profit)}</td>
                    <td>{formatPercentage(product.profitMargin)}</td>
                    <td>{formatCurrency(product.avgPrice)}</td>
                    <td>{product.customerCount}</td>
                    <td className="profit">{formatPercentage((product.totalCost || 0) > 0 ? (product.profit / product.totalCost) * 100 : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th>Cantidad</th>
                  <th>Prom. mensual</th>
                  <th>Ingresos</th>
                  <th>Costos</th>
                  <th>Ganancia</th>
                  <th>Margen %</th>
                  <th>Precio Prom.</th>
                  <th>Clientes</th>
                  <th>ROI</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCategories.map(category => (
                  <tr key={category.name}>
                    <td>{category.name}</td>
                    <td>{category.totalQuantity}</td>
                    <td>{(category.totalQuantity / numMonths).toFixed(1)}</td>
                    <td>{formatCurrency(category.totalRevenue)}</td>
                    <td>{formatCurrency(category.totalCost)}</td>
                    <td className="profit">{formatCurrency(category.profit)}</td>
                    <td>{formatPercentage(category.profitMargin)}</td>
                    <td>{formatCurrency(category.avgPrice)}</td>
                    <td>{category.customerCount}</td>
                    <td className="profit">{formatPercentage(category.totalCost > 0 ? (category.profit / category.totalCost) * 100 : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="filter-preset-btn"
            disabled={activePage <= 1}
            onClick={() => {
              if (isProductView) {
                setProductTablePage((prev) => Math.max(1, prev - 1));
              } else {
                setCategoryTablePage((prev) => Math.max(1, prev - 1));
              }
            }}
          >
            Anterior
          </button>
          <span style={{ color: uiTextColor, fontSize: 14 }}>
            Página {activePage} de {activeTotalPages}
          </span>
          <button
            type="button"
            className="filter-preset-btn"
            disabled={activePage >= activeTotalPages}
            onClick={() => {
              if (isProductView) {
                setProductTablePage((prev) => Math.min(productTotalPages, prev + 1));
              } else {
                setCategoryTablePage((prev) => Math.min(categoryTotalPages, prev + 1));
              }
            }}
          >
            Siguiente
          </button>
        </div>
      </div>
          </>
        );
      })()}
    </div>
  );
});

export const SalesReportTemporalView = memo(function SalesReportTemporalView({
  analyticsData,
  chartTextColor,
}) {
  return (
    <div className="temporal-analysis-container">
      <div className="analysis-header">
        <h3>Análisis Temporal y Tendencias</h3>
        <p>Patrones de venta por tiempo y estacionalidad</p>
      </div>

      <div className="temporal-charts">
        <div className="chart-card">
          <h4>Patrones por Hora del Día</h4>
          <div className="temporal-chart-wrapper">
            <Line data={{
              labels: Array.from({length: 24}, (_, i) => `${i}:00`),
              datasets: [{
                label: 'Ventas por Hora',
                data: Array.from({length: 24}, (_, i) => 
                  analyticsData.temporalAnalysis.hourly[i]?.revenue || 0
                ),
                borderColor: '#805ad5',
                backgroundColor: 'rgba(128, 90, 213, 0.1)',
                tension: 0.4,
                borderWidth: 2,
                fill: true
              }]
            }} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { labels: { color: chartTextColor } },
                tooltip: {
                  callbacks: {
                    afterBody: (context) => {
                      const idx = context[0]?.dataIndex;
                      if (idx == null) return [];
                      const h = analyticsData.temporalAnalysis.hourly[idx];
                      if (!h) return [];
                      return ['', `Operaciones: ${h.orders ?? 0}`, `Productos vendidos: ${h.products ?? 0}`];
                    }
                  }
                }
              },
              scales: {
                x: { ticks: { color: chartTextColor } },
                y: { ticks: { color: chartTextColor } }
              }
            }} />
          </div>
        </div>

        <div className="chart-card">
          <h4>Performance por Día de la Semana</h4>
          <div className="temporal-chart-wrapper">
            <Doughnut data={{
              labels: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
              datasets: [{
                data: [0, 1, 2, 3, 4, 5, 6].map(i => analyticsData.temporalAnalysis.dayOfWeek[i]?.revenue ?? 0),
                backgroundColor: [
                  '#e53e3e', '#d69e2e', '#38b2ac', '#4299e1',
                  '#805ad5', '#dd6b20', '#48bb78'
                ],
                borderWidth: 0
              }]
            }} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { labels: { color: chartTextColor } },
                tooltip: {
                  callbacks: {
                    afterBody: (context) => {
                      const idx = context[0]?.dataIndex;
                      if (idx == null) return [];
                      const d = analyticsData.temporalAnalysis.dayOfWeek[idx];
                      if (!d) return [];
                      return ['', `Operaciones: ${d.orders ?? 0}`, `Productos vendidos: ${d.products ?? 0}`];
                    }
                  }
                }
              }
            }} />
          </div>
        </div>
      </div>

      <div className="temporal-insights">
        <div className="insight-card">
          <h4>📊 Insights Temporales</h4>
          <ul>
            <li>
              <strong>Mejor día:</strong> {getDayName(
                parseInt(
                  Object.entries(analyticsData.temporalAnalysis.dayOfWeek)
                    .sort(([,a], [,b]) => (b?.revenue ?? 0) - (a?.revenue ?? 0))[0]?.[0],
                  10
                ) ?? 0
              )} con {formatCurrency(
                Math.max(...Object.values(analyticsData.temporalAnalysis.dayOfWeek).map(d => d?.revenue ?? 0))
              )}
            </li>
            <li>
              <strong>Horario pico:</strong> {
                Object.entries(analyticsData.temporalAnalysis.hourly)
                  .sort(([,a], [,b]) => b.revenue - a.revenue)[0]?.[0] || 'N/A'
              }:00 hs
            </li>
            <li>
              <strong>Mejor mes:</strong> {
                Object.entries(analyticsData.temporalAnalysis.monthly)
                  .sort(([,a], [,b]) => b.revenue - a.revenue)[0]?.[0] || 'N/A'
              }
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
});
